import { generateQueryParams } from 'util/queryUtils'
import { callTalousApi as callTalousApiReal } from './api/finance-api'
import { objectToSnakeCased } from 'util/changeCase'
import type { FetchOptions } from './api/util'
import type { SignedProxyTokenTypesEnum } from '@viilu-web/shared/types/viilu-talous'
import type { PurchaseInvoiceFilters } from 'ui/ManagerFinance/PurchaseInvoices/types'
import type { VoucherFilters } from 'ui/ManagerFinance/Accounting/Voucher/types'

const callTalousApi = (endpoint: string, options: FetchOptions) => {
	return callTalousApiReal(`/apiProxy${endpoint}`, options)
}

const COMPANY_UUID_HEADER = 'x-company-uuid'

type BaseParams = {
	companyUUID: string
}

type BasicGetFilters = BaseParams & {
	offset?: number
	limit?: number
}

type BasicGetFiltersWithDatabaseSource = BasicGetFilters & {
	databaseSource?: string
}

// VOUCHERS
export type GetVouchersParams = BasicGetFiltersWithDatabaseSource &
	VoucherFilters
export const _getVouchers = async (filters: GetVouchersParams) => {
	const { companyUUID, databaseSource, ...rest } = filters
	const queryParams = generateQueryParams(objectToSnakeCased(rest))

	const url = `/vouchers${
		databaseSource ? `/${databaseSource}` : ''
	}${queryParams}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetVoucherParams = BaseParams & {
	voucherId: number
	databaseSource: string
}
export const _getVoucher = async (params: GetVoucherParams) => {
	const { companyUUID, databaseSource, voucherId } = params

	const url = `/vouchers/${databaseSource}/${voucherId}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetVoucherRowsParams = BasicGetFiltersWithDatabaseSource
export const _getVoucherRows = async (filters: GetVoucherRowsParams) => {
	const { companyUUID, databaseSource, ...rest } = filters
	const queryParams = generateQueryParams(objectToSnakeCased(rest))

	const url = `/voucher_rows${
		databaseSource ? `/${databaseSource}` : ''
	}${queryParams}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetVoucherTypesParams = BasicGetFiltersWithDatabaseSource
export const _getVoucherTypes = async (filters: GetVoucherTypesParams) => {
	const { companyUUID, databaseSource, ...rest } = filters
	const queryParams = generateQueryParams(objectToSnakeCased(rest))

	const url = `/voucher_types${
		databaseSource ? `/${databaseSource}` : ''
	}${queryParams}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetVoucherTypeParams = BaseParams & {
	databaseSource: string
	id: number
}
export const _getVoucherType = async (params: GetVoucherTypeParams) => {
	const { companyUUID, databaseSource, id } = params
	const url = `/voucher_types/${databaseSource}/${id}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// COA
type GetChartOfAccountsParams = BasicGetFiltersWithDatabaseSource & {
	state?: number
	groupId?: number
}
export const _getChartOfAccounts = async (params: GetChartOfAccountsParams) => {
	const { companyUUID, databaseSource, ...filters } = params
	const queryParams = generateQueryParams(objectToSnakeCased(filters))

	const url = `/coa${databaseSource ? `/${databaseSource}` : ''}${queryParams}`
	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

type GetChartOfAccountParams = BaseParams & {
	accountId: number
	databaseSource: string
}
export const _getChartOfAccount = async (params: GetChartOfAccountParams) => {
	const { companyUUID, databaseSource, accountId } = params
	const url = `/coa/${databaseSource}/${accountId}`
	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// VAT
export type GetVatsParams = BasicGetFiltersWithDatabaseSource & {
	state?: number
}
export const _getVats = async (filters: GetVatsParams) => {
	const { companyUUID, databaseSource, ...rest } = filters
	const queryParams = generateQueryParams(objectToSnakeCased(rest))

	const url = `/vats${databaseSource ? `/${databaseSource}` : ''}${queryParams}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export const _getVatTypes = async (params: BaseParams) => {
	const { companyUUID } = params
	const url = `/vats/types`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetVatParams = BaseParams & {
	databaseSource: string
	vatId: number
}
export const _getVat = async (params: GetVatParams) => {
	const { companyUUID, databaseSource, vatId } = params
	const url = `/vats/${databaseSource}/${vatId}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// PURCHASE INVOICES
export type GetPurchaseInvoicesParams = BasicGetFiltersWithDatabaseSource &
	PurchaseInvoiceFilters
export const _getPurchaseInvoices = async (
	filters: GetPurchaseInvoicesParams,
) => {
	const { companyUUID, databaseSource, ...rest } = filters
	const queryParams = generateQueryParams(objectToSnakeCased(rest))

	const url = `/purchase_invoices${
		databaseSource ? `/${databaseSource}` : ''
	}${queryParams}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetPurchaseInvoiceParams = BaseParams & {
	purchaseInvoiceId: number
	databaseSource: string
}
export const _getPurchaseInvoice = async (params: GetPurchaseInvoiceParams) => {
	const { companyUUID, databaseSource, purchaseInvoiceId } = params

	const url = `/purchase_invoices/${databaseSource}/${purchaseInvoiceId}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetPurchaseInvoiceRows = BasicGetFiltersWithDatabaseSource
export const _getPurchaseInvoiceRows = async (
	filters: GetPurchaseInvoiceRows,
) => {
	const { companyUUID, ...rest } = filters
	const queryParams = generateQueryParams(objectToSnakeCased(rest))

	const url = `/purchase_invoice_rows${queryParams}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}
export type ConfirmPurchaseInvoiceParams = BaseParams & {
	purchaseInvoiceId: number
	databaseSource: string
}
export const _confirmPurchaseInvoice = async (
	params: ConfirmPurchaseInvoiceParams,
) => {
	const { companyUUID, databaseSource, purchaseInvoiceId } = params

	const url = `/purchase_invoices/${databaseSource}/${purchaseInvoiceId}/confirm`

	return callTalousApi(url, {
		method: 'PUT',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// COST CENTRES
type GetCostCentresParams = BasicGetFiltersWithDatabaseSource
export const _getCostCentres = async (params: GetCostCentresParams) => {
	const { companyUUID, databaseSource, ...filters } = params
	const queryParams = generateQueryParams(objectToSnakeCased(filters))

	const url = `/cost_centres${
		databaseSource ? `/${databaseSource}` : ''
	}${queryParams}`
	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

type GetCostCentreParams = BasicGetFiltersWithDatabaseSource & {
	costCentreId: number
}
export const _getCostCentre = async (params: GetCostCentreParams) => {
	const { companyUUID, databaseSource, costCentreId } = params
	const url = `/cost_centres/${databaseSource}/${costCentreId}`
	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// PAYMENT TERMS
type GetPaymentTermsParams = BasicGetFiltersWithDatabaseSource
export const _getPaymentTerms = async (params: GetPaymentTermsParams) => {
	const { companyUUID, databaseSource, ...filters } = params
	const queryParams = generateQueryParams(objectToSnakeCased(filters))

	const url = `/payment_terms${
		databaseSource ? `/${databaseSource}` : ''
	}${queryParams}`
	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

type GetPaymentTermParams = BasicGetFiltersWithDatabaseSource & {
	paymentTermId: number
}
export const _getPaymentTerm = async (params: GetPaymentTermParams) => {
	const { companyUUID, databaseSource, paymentTermId } = params
	const url = `/payment_terms/${databaseSource}/${paymentTermId}`
	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// SIGNED URL PROXY
export type MakeSignedProxyUrlParams = {
	type: SignedProxyTokenTypesEnum
	resourceId: number | string
	databaseSource: string
	companyUUID: string
}
export const _makeSignedProxyUrl = async (params: MakeSignedProxyUrlParams) => {
	const { type, companyUUID, databaseSource, resourceId } = params
	const url = `/makeSignedProxyUrl/${type}/${companyUUID}/${databaseSource}${
		resourceId ? `/${resourceId}` : ''
	}`
	return callTalousApiReal(url, {
		method: 'GET',
	})
}

// ATTACHMENTS
export type GetAttachmentsParams = BasicGetFiltersWithDatabaseSource
export const _getAttachments = async (filters: GetAttachmentsParams) => {
	const { companyUUID, databaseSource, ...rest } = filters
	const queryParams = generateQueryParams(objectToSnakeCased(rest))

	const url = `/attachments${
		databaseSource ? `/${databaseSource}` : ''
	}${queryParams}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

export type GetAttachmentParams = BaseParams & {
	attachmentId: number
	databaseSource: string
}
export const _getAttachment = async (params: GetAttachmentParams) => {
	const { companyUUID, databaseSource, attachmentId } = params

	const url = `/attachments/${databaseSource}/${attachmentId}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// INVOICES
export type GetInvoicesParams = BasicGetFiltersWithDatabaseSource
export const _getInvoices = async (params: GetInvoicesParams) => {
	const { databaseSource, companyUUID } = params
	const url = `/invoices/${databaseSource}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}

// CUSTOMERS
export type GetCustomerParams = BasicGetFiltersWithDatabaseSource
export const _getCustomers = async (params: GetInvoicesParams) => {
	const { databaseSource, companyUUID } = params
	const url = `/customers/${databaseSource}`

	return callTalousApi(url, {
		method: 'GET',
		headers: {
			[COMPANY_UUID_HEADER]: companyUUID,
		},
	})
}
